import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./ChatView.css";

/* ─── Robot Icon (inline) ─── */
const BotIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    <line x1="12" y1="3" x2="12" y2="7"/>
    <circle cx="8.5" cy="16" r="1.5" fill="currentColor" stroke="none"/>
    <circle cx="15.5" cy="16" r="1.5" fill="currentColor" stroke="none"/>
  </svg>
);

/* ─── Typing dots ─── */
function TypingIndicator() {
  return (
    <div className="ai-typing">
      <span /><span /><span />
    </div>
  );
}

/* ─── Message bubble ─── */
function Bubble({ msg, locale }) {
  const isUser = msg.role === "user";
  const time   = new Date(msg.timestamp).toLocaleTimeString(locale, {
    hour: "2-digit", minute: "2-digit",
  });
  return (
    <div className={`ai-msg ${isUser ? "me" : "bot"}`}>
      {!isUser && (
        <div className="ai-msg__avatar"><BotIcon /></div>
      )}
      <div className="ai-bubble">
        <div className="ai-bubble__text">{msg.content}</div>
        <div className="ai-bubble__time">{time}</div>
        {!isUser && msg.choices?.length > 0 && (
          <div className="ai-choices">
            {msg.choices.map((c, i) => (
              <button key={i} className="ai-choice-btn" onClick={() => msg.onChoice?.(c.value)}>
                {c.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Message counter badge ─────────────────────── */
function Counter({ remaining, upgraded, t }) {
  const isLow = remaining <= 2;
  return (
    <div className={`ai-counter ${isLow ? "ai-counter--low" : ""}`}>
      {upgraded && <span className="ai-counter__pro">⭐ {t("cycleBot.chatView.proBadge", { defaultValue: "Pro" })}</span>}
      <span>
        {remaining > 0
          ? t("cycleBot.chatView.messagesRemaining", { count: remaining })
          : t("cycleBot.chatView.limitReached")}
      </span>
    </div>
  );
}

/* ─── Mur Upgrade \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */
function UpgradeWall({ upgraded, onUpgrade, onReset, FREE_LIMIT, UPGRADE_BONUS, t }) {
  const [loading, setLoading] = useState(false);
  const [done,    setDone]    = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1500));
    setLoading(false);
    setDone(true);
    setTimeout(() => { onUpgrade(); }, 1200);
  };

  if (done) {
    return (
      <div className="cb-upgrade-wall cb-upgrade-wall--success">
        <div className="cb-upgrade-success-icon">🎉</div>
        <h2 className="cb-upgrade-title">{t("cycleBot.chatView.upgradeActivated")}</h2>
        <p className="cb-upgrade-desc">
          <strong>{t("cycleBot.chatView.upgradeBonusAdded", { count: UPGRADE_BONUS })}</strong><br/>
          {t("cycleBot.chatView.resumeConversationSoon")}
        </p>
        <div className="cb-upgrade-loading-dots"><span /><span /><span /></div>
      </div>
    );
  }

  return (
    <div className="cb-upgrade-wall">
      <div className="cb-upgrade-glow" aria-hidden="true" />
      <div className="cb-upgrade-icon">{loading ? "⏳" : "✨"}</div>
      <h2 className="cb-upgrade-title">
        {loading ? t("cycleBot.chatView.activating") : t("cycleBot.chatView.limitReached")}
      </h2>
      {!loading && (
        <>
          <p className="cb-upgrade-desc">
            {t("cycleBot.chatView.usedFreeMessages", { count: FREE_LIMIT })} <br/>
            {t("cycleBot.chatView.unlockBonus", { count: UPGRADE_BONUS })}
          </p>
          <div className="cb-upgrade-perks">
            {[
              { icon: "💬", label: t("cycleBot.chatView.perkExtraMessages") },
              { icon: "🧬", label: t("cycleBot.chatView.perkAdvancedAnalysis") },
              { icon: "📊", label: t("cycleBot.chatView.perkDetailedReports") },
              { icon: "🔔", label: t("cycleBot.chatView.perkCustomAlerts") },
            ].map((p) => (
              <div key={p.label} className="cb-upgrade-perk">
                <span className="cb-upgrade-perk-icon">{p.icon}</span>
                <span>{p.label}</span>
              </div>
            ))}
          </div>
          <div className="cb-upgrade-price">
            <span className="cb-upgrade-amount">9,99€</span>
            <span className="cb-upgrade-period">{t("cycleBot.chatView.perMonth")}</span>
          </div>
        </>
      )}
      {loading ? (
        <div className="cb-upgrade-spinner-wrap">
          <div className="cb-upgrade-spinner" />
          <p className="cb-upgrade-loading-text">{t("cycleBot.chatView.activatingAccess")}</p>
        </div>
      ) : (
        <>
          <button className="cb-upgrade-cta" onClick={handleUpgrade}>
            🚀 {t("cycleBot.chatView.unlockCta", { count: UPGRADE_BONUS })}
          </button>
          <button className="cb-upgrade-reset" onClick={onReset}>
            ↩ {t("cycleBot.chatView.resetFree")}
          </button>
        </>
      )}
    </div>
  );
}

/* ─── Vue principale ──────────────────────────── */
export default function ChatView({
  messages,
  onSend,
  onNewConversation,
  isTyping,
  chatLimit,
  embedded = false,
}) {
  const { t, i18n } = useTranslation("cycle");
  const locale = i18n.resolvedLanguage || i18n.language;
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  const {
    used        = 0,
    limit       = 5,
    upgraded    = false,
    remaining   = 5,
    limitReached = false,
    incrementUsed,
    doUpgrade,
    resetLimit,
    FREE_LIMIT   = 5,
    UPGRADE_BONUS = 5,
  } = chatLimit || {};

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isTyping]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || limitReached) return;
    setInput("");
    incrementUsed?.();
    onSend(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleReset = () => {
    resetLimit?.();
    onNewConversation?.();
  };

  const handleUpgrade = () => {
    doUpgrade?.();
  };

  return (
    <div className="ai-chat">
      {/* ── Header ── */}
      <div className="ai-chat__header">
        <div className="ai-chat__header-left">
          <div className="ai-chat__avatar-wrap">
            <BotIcon />
            <span className="ai-chat__online-dot" />
          </div>
          <div className="ai-chat__header-info">
            <span className="ai-chat__title">{t("cycleBot.chatView.title")}</span>
            <span className="ai-chat__subtitle">{t("cycleBot.chatView.subtitle")}</span>
          </div>
        </div>
        <button
          className="ai-chat__new-btn"
          onClick={handleReset}
          title={t("cycleBot.chatView.newConversation")}
          aria-label={t("cycleBot.chatView.newConversation")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6"  x2="6"  y2="18"/>
            <line x1="6"  y1="6"  x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* ── Messages ── */}
      {limitReached ? (
        <UpgradeWall
          key={limit}
          upgraded={upgraded}
          onUpgrade={handleUpgrade}
          onReset={handleReset}
          FREE_LIMIT={FREE_LIMIT}
          UPGRADE_BONUS={UPGRADE_BONUS}
          t={t}
        />
      ) : (
        <>
          <div className="ai-chat__body" ref={scrollRef}>
            {messages.length === 0 ? (
              <div className="ai-welcome">
                <div className="ai-bubble bot">
                  <div className="ai-bubble__text">
                    {t("cycleBot.chatView.welcomeTitle")} 👋
                  </div>
                </div>
                <Counter remaining={remaining} upgraded={upgraded} t={t} />
              </div>
            ) : (
              <>
                {messages.map((m, i) => <Bubble key={i} msg={m} locale={locale} />)}
                <Counter remaining={remaining} upgraded={upgraded} t={t} />
                {isTyping && <TypingIndicator />}
              </>
            )}
          </div>

          {/* ── Input row ── */}
          <div className="ai-chat__input-row">
            <input
              type="text"
              className="ai-chat__input"
              placeholder={t("cycleBot.chatView.inputPlaceholder", { count: remaining })}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={limitReached}
            />
            <button
              className="ai-chat__send-btn"
              onClick={handleSend}
              disabled={limitReached || !input.trim()}
              aria-label="Send"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5"  y1="12" x2="19" y2="12"/>
              </svg>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
